import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconFolder, IconPrinter } from "@tabler/icons-react";
import {
  cassetteLabelPrinterService,
  type CassettePrinterStatus,
} from "../../../services/cassetteLabelPrinter.service";
import {
  slideLabelPrinterService,
  type PrinterStatus as SlidePrinterStatus,
} from "../../../services/slideLabelPrinter.service";

interface PrinterPreferencesModalProps {
  opened: boolean;
  onClose: () => void;
}

export function PrinterPreferencesModal({
  opened,
  onClose,
}: PrinterPreferencesModalProps) {
  const [cassetteStatus, setCassetteStatus] = useState<CassettePrinterStatus>(
    cassetteLabelPrinterService.status
  );
  const [slideStatus, setSlideStatus] = useState<SlidePrinterStatus>(
    slideLabelPrinterService.status
  );

  useEffect(() => {
    const unsubscribeCassette =
      cassetteLabelPrinterService.onStatusChange(setCassetteStatus);
    const unsubscribeSlide =
      slideLabelPrinterService.onStatusChange(setSlideStatus);
    setCassetteStatus(cassetteLabelPrinterService.status);
    setSlideStatus(slideLabelPrinterService.status);
    return () => {
      unsubscribeCassette();
      unsubscribeSlide();
    };
  }, []);

  const handleSelectCassetteFolder = useCallback(async () => {
    const selected = await cassetteLabelPrinterService.selectOutputDirectory();
    if (!selected) return;
    notifications.show({
      title: "Cassette Folder Set",
      message: `Output: "${cassetteLabelPrinterService.folderName}"`,
      color: "green",
    });
  }, []);

  const handleSelectSlideFolder = useCallback(async () => {
    const selected = await slideLabelPrinterService.selectOutputDirectory();
    if (!selected) return;
    notifications.show({
      title: "Slide Folder Set",
      message: `Output: "${slideLabelPrinterService.folderName}"`,
      color: "green",
    });
  }, []);

  const cassetteFolder = useMemo(
    () =>
      cassetteStatus === "folder-selected"
        ? cassetteLabelPrinterService.folderName
        : null,
    [cassetteStatus]
  );
  const slideFolder = useMemo(
    () =>
      slideStatus === "folder-selected"
        ? slideLabelPrinterService.folderName
        : null,
    [slideStatus]
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Printer Preferences"
      size="md"
    >
      <Stack gap="lg">
        <Box
          p="md"
          style={{
            borderRadius: "var(--mantine-radius-md)",
            border: "1px solid var(--mantine-color-gray-2)",
            background: "var(--mantine-color-gray-0)",
          }}
        >
          <Group gap="sm" mb="sm">
            <ThemeIcon variant="light" color="blue" size="md" radius="md">
              <IconPrinter size={16} />
            </ThemeIcon>
            <Text fw={700} size="sm">
              Cassette Label Printer
            </Text>
          </Group>
          <Group justify="space-between" align="center">
            <Box>
              <Text size="xs" c="dimmed">
                Output Folder
              </Text>
              <Text size="sm" fw={500}>
                {cassetteStatus === "folder-selected" && cassetteFolder
                  ? cassetteFolder
                  : cassetteStatus === "unsupported"
                  ? "Not supported in this browser"
                  : "No folder selected"}
              </Text>
            </Box>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconFolder size={14} />}
              onClick={handleSelectCassetteFolder}
              disabled={cassetteStatus === "unsupported"}
            >
              {cassetteStatus === "folder-selected"
                ? "Change Folder"
                : "Select Folder"}
            </Button>
          </Group>
        </Box>

        <Box
          p="md"
          style={{
            borderRadius: "var(--mantine-radius-md)",
            border: "1px solid var(--mantine-color-gray-2)",
            background: "var(--mantine-color-gray-0)",
          }}
        >
          <Group gap="sm" mb="sm">
            <ThemeIcon variant="light" color="violet" size="md" radius="md">
              <IconPrinter size={16} />
            </ThemeIcon>
            <Text fw={700} size="sm">
              Slide Label Printer
            </Text>
          </Group>
          <Group justify="space-between" align="center">
            <Box>
              <Text size="xs" c="dimmed">
                Output Folder
              </Text>
              <Text size="sm" fw={500}>
                {slideStatus === "folder-selected" && slideFolder
                  ? slideFolder
                  : slideStatus === "unsupported"
                  ? "Not supported in this browser"
                  : "No folder selected"}
              </Text>
            </Box>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconFolder size={14} />}
              onClick={handleSelectSlideFolder}
              disabled={slideStatus === "unsupported"}
            >
              {slideStatus === "folder-selected"
                ? "Change Folder"
                : "Select Folder"}
            </Button>
          </Group>
        </Box>

        <Group justify="flex-end" mt="md">
          <Button onClick={onClose}>Done</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
